/**
 * 신포맷(SBC1) 첨부 스트리밍 복호 — 설계 §5.1 downloadManager
 *
 * format.js 의 decodeFile 은 객체 전체를 메모리에 올리므로(그 파일 :192-193 경고) 대용량에
 * 쓸 수 없다. 여기서는 청크 프리미티브(parseHeader/deriveKey/decryptChunk)를 직접 조합해
 * **스트림에서 청크 경계만큼만** 버퍼링한다 — 상주 메모리가 O(청크) 로 묶인다.
 *
 * ⚠️ 의존 방향: 이 모듈은 Attachment/helper.js 를 **절대 import 하지 않는다**(설계 :200-202).
 * 포맷 판별과 구포맷 폴백 분기는 helper 쪽 책임이다. 역-import 하면 순환이 된다.
 */
import {
  HEADER_SIZE,
  IV_SIZE,
  MAX_CHUNK_SIZE,
  MIN_SAFE_CHUNK_SIZE,
  TAG_SIZE,
  ChunkFormatError,
  chunkCount,
  decryptChunk,
  deriveKey,
  isChunked,
  parseHeader,
} from './format'

const CHUNK_OVERHEAD = IV_SIZE + TAG_SIZE

/**
 * 입력을 ReadableStream 으로 정규화한다.
 *
 * presigned 경로는 `fetch` 의 Response(스트림 가능), 프록시 폴백은 XHR 이라 Blob 뿐이다
 * (helper.js:129-137). Blob.stream() 으로 양쪽을 같은 경로에 태운다.
 */
const toStream = (source) => {
  if (source && typeof source.getReader === 'function') return source
  if (source && source.body && typeof source.body.getReader === 'function') return source.body
  if (source && typeof source.stream === 'function') return source.stream()
  throw new ChunkFormatError('BAD_SOURCE', 'source must be a ReadableStream, Response, or Blob')
}

/** 헤더가 신고한 chunkSize 를 소비 시점에 검증한다 (설계 :60-62 — 코덱은 상한만 강제). */
const assertUsableChunkSize = (chunkSize) => {
  if (chunkSize < MIN_SAFE_CHUNK_SIZE || chunkSize > MAX_CHUNK_SIZE) {
    // 테스트 벡터용 16B 같은 극소 청크가 실파일로 오면 청크 수가 폭증해 사실상 DoS 가 된다.
    throw new ChunkFormatError(
      'UNSAFE_CHUNK_SIZE',
      `chunkSize ${chunkSize} outside [${MIN_SAFE_CHUNK_SIZE}, ${MAX_CHUNK_SIZE}]`,
    )
  }
}

/**
 * 스트림에서 정확히 `size` 바이트를 모아 반환한다. 스트림이 먼저 끝나면 null.
 *
 * 남은 바이트는 carry 로 넘겨 다음 호출이 이어 쓴다 — 청크 경계와 네트워크 패킷 경계가
 * 일치하지 않으므로 이 재조립이 필수다.
 */
const readExactly = async (reader, size, state) => {
  while (state.buffered < size) {
    // 순차 소비가 목적 (상주 메모리를 청크 하나로 묶는다)
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read()
    if (done) return null
    if (value && value.length > 0) {
      state.parts.push(value)
      state.buffered += value.length
    }
  }

  const out = new Uint8Array(size)
  let filled = 0
  while (filled < size) {
    const head = state.parts[0]
    const need = size - filled
    if (head.length <= need) {
      out.set(head, filled)
      filled += head.length
      state.parts.shift()
    } else {
      out.set(head.subarray(0, need), filled)
      state.parts[0] = head.subarray(need)
      filled += need
    }
  }
  state.buffered -= size
  return out
}

/**
 * 신포맷 객체를 스트리밍 복호해 평문 Blob 을 만든다.
 *
 * @param {ReadableStream|Response|Blob} source 암호화된 객체 바이트 출처
 * @param {string} password 키 재료 = rsKeyObj.rsKey (바이트 문자열)
 * @param {object} [options]
 * @param {(progress: {chunkIndex:number, totalChunks:number, bytes:number, totalBytes:number, ratio:number}) => void} [options.onProgress]
 * @param {AbortSignal} [options.signal] 미리보기 이탈 등으로 취소할 때
 * @param {string} [options.type] 결과 Blob 의 MIME
 * @returns {Promise<Blob>} 평문 Blob
 */
export const decodeStream = async (source, password, options = {}) => {
  const { onProgress, signal, type = 'application/octet-stream' } = options
  const reader = toStream(source).getReader()
  const state = { parts: [], buffered: 0 }

  const throwIfAborted = () => {
    if (signal && signal.aborted) {
      throw new ChunkFormatError('ABORTED', 'download cancelled')
    }
  }

  try {
    const headerBytes = await readExactly(reader, HEADER_SIZE, state)
    if (headerBytes === null) {
      throw new ChunkFormatError('TRUNCATED', 'stream ended before header')
    }
    if (!isChunked(headerBytes)) {
      // 호출부(helper)가 판별을 선행하지만, 스트림 선두만 보고 넘긴 경우를 위한 이중 방어
      throw new ChunkFormatError('NOT_CHUNKED', 'not an SBC1 chunked object')
    }

    const { chunkSize, totalSize, salt } = parseHeader(headerBytes)
    assertUsableChunkSize(chunkSize)

    const totalChunks = chunkCount(totalSize, chunkSize)
    const headerInfo = { chunkSize, totalSize }
    const key = await deriveKey(password, salt)

    // ⚠️ 평문을 Uint8Array 배열로 모으면 **전량이 JS 힙에 동시 상주**해, 이 설계가 없애려는
    // OOM(500MB 다운로드 탭 크래시, §1.2)이 그대로 재현된다. 복호 즉시 청크를 Blob 으로
    // 감싸 브라우저 blob 저장소(힙 밖, 필요 시 디스크)로 넘기고 Uint8Array 참조를 버린다.
    // → 상주 힙이 O(청크 1개)로 묶이고, 마지막 new Blob(blobParts) 는 조각 참조만 엮는다.
    const blobParts = []
    let plainBytes = 0

    for (let i = 0; i < totalChunks; i += 1) {
      throwIfAborted()
      const plainLen = Math.min(chunkSize, totalSize - plainBytes)
      // 순차 복호가 목적 — 병렬화하면 상주 메모리가 청크 수에 비례해 늘어난다
      // eslint-disable-next-line no-await-in-loop
      const record = await readExactly(reader, plainLen + CHUNK_OVERHEAD, state)
      if (record === null) {
        throw new ChunkFormatError('TRUNCATED', `stream ended at chunk ${i}/${totalChunks}`)
      }
      // eslint-disable-next-line no-await-in-loop
      const plain = await decryptChunk(key, headerInfo, i, record)
      blobParts.push(new Blob([plain]))
      plainBytes += plain.length

      // 진행률은 부가 정보다 — 소비자 콜백이 던져도(언마운트 후 setState 등) 복호를 실패시키지 않는다
      if (onProgress) {
        try {
          onProgress({
            chunkIndex: i,
            totalChunks,
            bytes: plainBytes,
            totalBytes: totalSize,
            ratio: totalSize === 0 ? 1 : plainBytes / totalSize,
          })
        } catch (e) {
          // 무시 — 진행률 표시 실패가 파일 복호를 막아서는 안 된다
        }
      }
    }

    // 헤더가 신고한 크기와 실제 복호량이 어긋나면 절단·위조다 (format.decodeFile :220-227 과 동형)
    if (plainBytes !== totalSize) {
      throw new ChunkFormatError(
        'SIZE_MISMATCH',
        `expected ${totalSize} plain bytes, got ${plainBytes}`,
      )
    }
    // 선언된 청크를 다 읽은 뒤 잔여 바이트가 남아 있으면 패딩된 객체다
    const tail = await reader.read()
    if (!tail.done && tail.value && tail.value.length > 0) {
      throw new ChunkFormatError('SIZE_MISMATCH', 'unexpected trailing bytes after last chunk')
    }
    if (state.buffered > 0) {
      throw new ChunkFormatError('SIZE_MISMATCH', 'unexpected buffered bytes after last chunk')
    }

    return new Blob(blobParts, { type })
  } finally {
    // 정상·에러·취소 모두에서 네트워크 연결을 놓아준다
    try {
      await reader.cancel()
    } catch (e) {
      // 이미 종료된 스트림 cancel 은 무해
    }
  }
}

/**
 * helper 가 넘긴 Blob/File 을 신포맷으로 복호해 File 로 되돌린다.
 *
 * 기존 `attachment.decode` 의 반환 계약(File)을 그대로 유지해 호출부 변경을 최소화한다
 * (설계 :212 "분기 최소 수정").
 */
export const decodeToFile = async (source, password, name, options = {}) => {
  const blob = await decodeStream(source, password, options)
  return new File([blob], name, { type: options.type || 'application/octet-stream' })
}

/**
 * Blob 을 소비하지 않고 헤더만 떼어 신포맷인지 판별한다 (helper 분기용).
 *
 * `isChunked` 는 magic 4B 외에 version·cipher(바이트 4·5)까지 보고 **길이가 HEADER_SIZE 이상**
 * 이어야 true 를 준다(format.js:77,85). 그래서 4바이트가 아니라 헤더 전체를 떼어 넘긴다.
 * Blob 은 재판독 가능하므로 이 slice 가 이후 decodeStream 입력을 소비하지 않는다.
 */
export const peekIsChunked = async (blob) => {
  if (!blob || typeof blob.slice !== 'function') return false
  try {
    const head = blob.slice(0, HEADER_SIZE)
    if (!head || typeof head.arrayBuffer !== 'function') return false
    return isChunked(new Uint8Array(await head.arrayBuffer()))
  } catch (e) {
    // 판별 자체가 실패하면 **구포맷으로 강등**한다 (설계 §2.3 "파싱 실패 → 구포맷 재시도").
    // 여기서 throw 하면 판별 실패가 다운로드 전체 실패로 번진다. 실브라우저는 Blob.arrayBuffer
    // 를 모두 지원하므로 이 경로는 jsdom 등 축약 구현에서만 타며, 그때 기존 경로가 처리한다.
    return false
  }
}
